import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {fileLevelCommentKey, type FileKey, type LineKey} from '@github-ui/diff-lines/types'
import {positioningToLineKey, toThreadSubjectType} from '@github-ui/diff-lines/thread-positioning'
import {OUTSIDE_DIFF_KEY} from '../../helpers/thread-positioning'
import {useGetThreadFromThreadID, useMarkersActions} from '../../stores/selectors'
import type {ReviewMenuData} from '../payloads/review-menu'

export function useReviewMenuData(pendingThreadIds?: number[], options?: {enabled?: boolean}) {
  const enabled = options?.enabled ?? true
  const params = pendingThreadIds?.length ? new URLSearchParams() : undefined
  if (pendingThreadIds && params) {
    for (const id of pendingThreadIds) {
      params.append('threadIds[]', String(id))
    }
  }
  const reviewMenuURL = usePageDataUrl(PageData.reviewMenu, params?.toString())
  const getThreadFromThreadID = useGetThreadFromThreadID()
  const {updateThread} = useMarkersActions()

  return useQuery<ReviewMenuData>({
    queryKey: [PageData.reviewMenu, reviewMenuURL],
    queryFn: async () => {
      const result = await reactFetchJSON(reviewMenuURL)
      if (!result.ok) throw new Error(`HTTP ${result.status}: ${result.statusText}`)
      const json = (await result.json()) as ReviewMenuData

      if (json.threads) {
        seedThreads(json.threads, getThreadFromThreadID, updateThread)
      }

      return json
    },
    staleTime: Infinity,
    enabled,
  })
}

function seedThreads(
  threads: Thread[],
  getThreadFromThreadID: (threadID: number) => Thread | undefined,
  updateThread: (
    id: number,
    filePath: string,
    lineKey: LineKey | FileKey,
    transform: (old: Thread | undefined) => Thread | undefined,
  ) => void,
) {
  for (const thread of threads) {
    const threadId = Number(thread.id)
    if (getThreadFromThreadID(threadId)) continue

    const currentRange = thread.currentPositioning
    const subjectType = toThreadSubjectType(thread.subjectType)
    const seededThread = {
      ...thread,
      subjectType,
    }

    if (currentRange) {
      const lineKey = positioningToLineKey(currentRange) ?? fileLevelCommentKey
      updateThread(threadId, currentRange.path, lineKey, () => seededThread)
    } else {
      updateThread(threadId, OUTSIDE_DIFF_KEY, OUTSIDE_DIFF_KEY, () => seededThread)
    }
  }
}
